import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoIosArrowDown, IoIosArrowForward } from 'react-icons/io';

import {
  BannerTrasparency,
  Collapse,
  Container,
  FolderRow,
  NurseryTitle,
} from './styles';
import api from '~/services/api';
import Loading from '~/components/Loading';
import NoData from '~/components/NoData';
import Regulations from '../Regulations';

interface IArchive {
  id: number;
  title: string;
  archive_url: string;
}

interface IFolder {
  id: number;
  name: string;
  archives: IArchive[];
  opened: boolean;
}

interface INursery {
  id: number;
  name: string;
  folders: IFolder[];
  opened: boolean;
}

const TransparencyPortal: React.FC = () => {
  const [nurseries, setNurseries] = useState<INursery[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<INursery[]>('nurseries', {
        params: {
          type: 'TransparencyPortal',
        },
      })
      .then((response) => {
        const data = response.data.map((nursery) => ({
          ...nursery,
          opened: true,
          folders: (nursery.folders || [])
            .slice()
            .sort((a: any, b: any) => a.order - b.order)
            .map((folder) => ({
              ...folder,
              archives: folder.archives || [],
              opened: false,
            })),
        }));

        setNurseries(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const data = useMemo(() => {
    let index = 0;
    let color = '';

    return nurseries.map((nursery) => {
      switch (index) {
        case 1:
          color = '#224B8F';
          break;
        case 2:
          color = '#B76522';
          index = -1;
          break;

        default:
          color = '#00A189';
          break;
      }

      index += 1;

      return {
        ...nursery,
        color,
      };
    });
  }, [nurseries]);

  const handleClickNursery = useCallback((nurseryIndex) => {
    setNurseries((state) => {
      const newNurseries = state.slice();
      newNurseries[nurseryIndex] = {
        ...newNurseries[nurseryIndex],
        opened: !newNurseries[nurseryIndex].opened,
      };
      return newNurseries;
    });
  }, []);

  const handleClickFolder = useCallback((nurseryIndex, folderIndex) => {
    setNurseries((state) => {
      const newNurseries = state.slice();
      const newFolders = newNurseries[nurseryIndex].folders.slice();
      newFolders[folderIndex] = {
        ...newFolders[folderIndex],
        opened: !newFolders[folderIndex].opened,
      };
      newNurseries[nurseryIndex] = {
        ...newNurseries[nurseryIndex],
        folders: newFolders,
      };
      return newNurseries;
    });
  }, []);

  return (
    <Container>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h1 className="h4 h1-lg mb-4 mb-lg-5">Portal da Transparência</h1>
            <p className="h6 h4-lg fw-light mb-4 mb-lg-5 pb-lg-5">
              O <b>Portal da Transparência</b> do Instituto Barueri tem a
              finalidade de demonstrar dados e informações detalhados sobre a
              execução orçamentária e financeira, objetivando o gerenciamento,
              operacionalização e execução das ações e serviços de educação nas
              maternais. A Prestação de Contas é um dever do Instituto, e o
              acesso irrestrito às mesmas é um direito do cidadão.
            </p>
          </div>
          {data.length > 0 ? (
            <>
              {data.map((nursery, nurseryIndex) => (
                <>
                  <div className="col-12">
                    <NurseryTitle
                      type="button"
                      color={nursery.color}
                      opened={nursery.opened}
                      className="w-100 d-flex justify-content-between align-items-center mb-4 py-3 px-4"
                      onClick={() => handleClickNursery(nurseryIndex)}
                    >
                      <h2 className="h5 h4-lg fw-normal mb-0">
                        {nursery.name}
                      </h2>
                      <IoIosArrowDown size={24} className="arrow" />
                    </NurseryTitle>
                  </div>
                  <Collapse opened={nursery.opened} className="col-12">
                    <div className="pb-4 pb-lg-5">
                      {nursery.folders.length > 0 ? (
                        nursery.folders.map((folder, folderIndex) => (
                          <React.Fragment key={folder.id}>
                            <FolderRow
                              type="button"
                              color={nursery.color}
                              opened={folder.opened}
                              className="w-100 d-flex align-items-center mb-3 py-3 px-4"
                              onClick={() =>
                                handleClickFolder(nurseryIndex, folderIndex)
                              }
                            >
                              <IoIosArrowForward
                                size={18}
                                className="arrow me-2"
                              />
                              <span>{folder.name}</span>
                            </FolderRow>
                            <Collapse opened={folder.opened}>
                              <div className="d-flex flex-wrap justify-content-start px-xxl-4">
                                {folder.archives.length > 0 ? (
                                  folder.archives.map((archive) => (
                                    <BannerTrasparency
                                      color={nursery.color}
                                      className="mb-4 mb-lg-5"
                                      key={archive.id}
                                    >
                                      <a
                                        href={archive.archive_url}
                                        className="h-100 d-flex flex-column justify-content-end px-4 py-5 px-xl-5"
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        <h5 className="h5 fw-bold text-white">
                                          {archive.title}
                                        </h5>
                                      </a>
                                    </BannerTrasparency>
                                  ))
                                ) : (
                                  <div className="col-12 mb-5">
                                    <NoData message="Nenhum documento cadastrado" />
                                  </div>
                                )}
                              </div>
                            </Collapse>
                          </React.Fragment>
                        ))
                      ) : (
                        <div className="col-12 mb-5">
                          <NoData message="Nenhuma pasta cadastrada" />
                        </div>
                      )}
                    </div>
                  </Collapse>
                </>
              ))}
            </>
          ) : (
            <div className="col-12 mb-5">
              <NoData message="Nenhum relatório cadastrado" />
            </div>
          )}
        </div>
      </div>
      <Regulations />
      <Loading active={loading} />
    </Container>
  );
};

export default TransparencyPortal;
